import { useRef, type KeyboardEvent } from 'react';

export interface Pestana<T extends string> {
  id: T;
  texto: string;
  cantidad?: number;
}

interface PropsPestanas<T extends string> {
  pestanas: readonly Pestana<T>[];
  activa: T;
  alElegir: (id: T) => void;
  etiqueta?: string;
}

function destinoDeTecla(tecla: string, actual: number, total: number): number | null {
  switch (tecla) {
    case 'ArrowRight': return (actual + 1) % total;
    case 'ArrowLeft': return (actual - 1 + total) % total;
    case 'Home': return 0;
    case 'End': return total - 1;
    default: return null;
  }
}

// Solo la pestaña activa entra en el orden de tabulación; las flechas mueven entre
// pestañas, como indica el patrón de ARIA para listas de pestañas.
export function Pestanas<T extends string>({ pestanas, activa, alElegir, etiqueta }: PropsPestanas<T>) {
  const lista = useRef<HTMLDivElement>(null);

  function alTeclear(e: KeyboardEvent<HTMLDivElement>) {
    const actual = pestanas.findIndex((p) => p.id === activa);
    const destino = destinoDeTecla(e.key, actual, pestanas.length);
    const pestana = destino === null ? undefined : pestanas[destino];
    if (destino === null || !pestana) return;
    e.preventDefault();
    alElegir(pestana.id);
    lista.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[destino]?.focus();
  }

  return (
    <div ref={lista} className="pestanas" role="tablist" aria-label={etiqueta} onKeyDown={alTeclear}>
      {pestanas.map((p) => {
        const esActiva = p.id === activa;
        return (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={esActiva}
            tabIndex={esActiva ? 0 : -1}
            className={esActiva ? 'pestanas__pestana pestanas__pestana--activa' : 'pestanas__pestana'}
            onClick={() => { alElegir(p.id); }}
          >
            {p.texto}
            {p.cantidad !== undefined && <span className="pestanas__cantidad">{p.cantidad}</span>}
          </button>
        );
      })}
    </div>
  );
}
